/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { DEFAULT_FILTERS, selectFilters } from '@zcode-ui/filters'
import { PROP_NAMES } from '@zcode/mixpanel'
import { selectNearbyHoods } from '@zcode/nr/es/shared/modules/nearbyHoods/selectors'
import type { State } from '@zcode/state-type'
import deepequal from 'fast-deep-equal'
import { logger } from '../../../logger'

import { isErrorish } from '@zcode/error-filters'
import { isNumber, isTruthy } from '@zcode/utils'
import { mapAmenities } from './mapAmenities'
import { mapBedrooms } from './mapBedrooms'
import { mapNeighborhoods } from './mapNeighborhoods'
import { mapOrFeatures } from './mapOrFeatures'
import { mapOtherFilters } from './mapOtherFilters'
import { mapPets } from './mapPets'
import { mapPropertyCategories } from './mapPropertyCategories'

const ratingTextMap: { [key: number]: string } = { 9: 'Excellent', 7: 'Very good', 5: 'Good' }

export const getFilterPropObject = (state: State) => {
  const filters = selectFilters(state) || {}
  const defaultFilters = DEFAULT_FILTERS

  if (deepequal(filters, defaultFilters)) {
    return {}
  }

  const {
    bathroomsRange,
    bedrooms,
    buildingAmenities,
    cats,
    dogs,
    floorplans,
    longTerm,
    listingAmenities,
    maxPrice,
    minPrice,
    neighborhoodIds,
    orFeatures,
    propertyTypes,
    rating,
    shortTerm,
  } = filters

  const filterProps: any = {}

  try {
    if (shortTerm !== DEFAULT_FILTERS.shortTerm) {
      const leaseLength = (
        [longTerm ? 'Long term' : false, shortTerm ? 'Short term' : false] as const
      ).filter(isTruthy)
      if (leaseLength?.length) {
        filterProps[PROP_NAMES.LEASE_LENGTH_FILTER] = leaseLength
      }
    }
    if (orFeatures?.[0]?.length) {
      const [features] = orFeatures
      filterProps[PROP_NAMES.PROPERTY_FEATURE_FILTER] = mapOrFeatures(features)
    }

    if (!deepequal(bedrooms, defaultFilters.bedrooms)) {
      filterProps[PROP_NAMES.BEDROOM_FILTER] = mapBedrooms(bedrooms)
    }

    if (cats || dogs) {
      filterProps[PROP_NAMES.PETS_FILTER] = mapPets(filters)
    }

    if (floorplans === 0) {
      filterProps[PROP_NAMES.BUILDING_FILTER] = 'Hide buildings'
    } else if (floorplans === 1) {
      filterProps[PROP_NAMES.BUILDING_FILTER] = 'Only buildings'
    }

    if (
      !deepequal(listingAmenities, defaultFilters.listingAmenities) ||
      !deepequal(buildingAmenities, defaultFilters.buildingAmenities)
    ) {
      filterProps[PROP_NAMES.AMENITIES_FILTER] = mapAmenities(listingAmenities, buildingAmenities)
    }

    if (!deepequal(propertyTypes, defaultFilters.propertyTypes)) {
      filterProps[PROP_NAMES.TYPE_FILTER] = mapPropertyCategories(propertyTypes)
    }

    if (maxPrice !== defaultFilters.maxPrice) {
      filterProps[PROP_NAMES.MAX_PRICE_FILTER] = maxPrice
    }

    if (minPrice !== defaultFilters.minPrice) {
      filterProps[PROP_NAMES.MIN_PRICE_FILTER] = minPrice
    }

    if (bathroomsRange?.length) {
      filterProps[PROP_NAMES.BATHROOM_FILTER] = bathroomsRange
    }

    if (!deepequal(neighborhoodIds, defaultFilters.neighborhoodIds)) {
      const nearbyHoods = selectNearbyHoods(state)
      if (nearbyHoods?.length) {
        filterProps[PROP_NAMES.NEIGHBORHOOD_FILTER] = mapNeighborhoods(nearbyHoods, neighborhoodIds)
      }
    }

    const { min } = rating
    if (isNumber(min)) {
      filterProps[PROP_NAMES.RATING_FILTER] = ratingTextMap[min]
    }

    const other = mapOtherFilters(filters)
    if (other?.length) {
      filterProps[PROP_NAMES.OTHER_FILTER] = other
    }
  } catch (e: unknown) {
    if (isErrorish(e)) {
      logger.error(`Error gathering filter props for mixpanel: ${e.message}`)
    }
  }

  return filterProps
}
